// ============================================================================
// SupplyParUpdateCard — propose_supply_par_update. UPSERT into
// facility_supply_allocation, keyed by (facility_id, supply_id).
// 2026-05-21 Cowork: AM Co-pilot v2 action expansion.
// ============================================================================

import { useMemo, useState } from 'react';
import { Boxes } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface SupplyParUpdateCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function SupplyParUpdateCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: SupplyParUpdateCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    supply_id?: string;
    par_level?: number | null;
    reorder_threshold?: number | null;
    notes?: string | null;
  };

  const [facility_id, setFacilityId] = useState(initial.facility_id ?? '');
  const [supplyId, setSupplyId] = useState(initial.supply_id ?? '');
  const [parLevel, setParLevel] = useState<string>(
    initial.par_level != null ? String(initial.par_level) : '',
  );
  const [reorderThreshold, setReorderThreshold] = useState<string>(
    initial.reorder_threshold != null ? String(initial.reorder_threshold) : '',
  );
  const [notes, setNotes] = useState(initial.notes ?? '');

  const wasEdited = useMemo(() => {
    return (
      facility_id !== (initial.facility_id ?? '') ||
      supplyId !== (initial.supply_id ?? '') ||
      (parLevel ? Number(parLevel) : null) !== (initial.par_level ?? null) ||
      (reorderThreshold ? Number(reorderThreshold) : null) !== (initial.reorder_threshold ?? null) ||
      (notes || null) !== (initial.notes ?? null)
    );
  }, [facility_id, supplyId, parLevel, reorderThreshold, notes, initial]);

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        supply_id: supplyId,
        par_level: parLevel ? Number(parLevel) : null,
        reorder_threshold: reorderThreshold ? Number(reorderThreshold) : null,
        notes: notes || null,
      },
      wasEdited,
    );
  };

  return (
    <CardShell
      title="Supply Par Update"
      icon={<Boxes className="h-4 w-4" />}
      status={proposal.status}
      committedLabel={proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'}
      isCommitting={isCommitting}
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <Field label="Facility">
        <FacilityCombobox value={facility_id} options={facilities} onChange={setFacilityId} />
      </Field>
      <Field label="Supply (SUP-XXXX)">
        <input
          value={supplyId}
          onChange={(e) => setSupplyId(e.target.value.toUpperCase())}
          className={inputClass}
          placeholder="SUP-0001"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Par level">
          <input
            type="number"
            step="0.01"
            value={parLevel}
            onChange={(e) => setParLevel(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Reorder threshold">
          <input
            type="number"
            step="0.01"
            value={reorderThreshold}
            onChange={(e) => setReorderThreshold(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          value={notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={`${inputClass} resize-none`}
        />
      </Field>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
